/**
 * Per-term depth for glossary pages.
 *
 * Each definition page also gets: the wording a homeowner will actually see in
 * a title commitment or plat, the mistake people make with that term, and the
 * concrete next steps. Keeping it in a separate file leaves the core
 * definitions in `easementGlossary.ts` short and quotable.
 */

export type GlossaryDepth = {
  /** Verbatim-style example of how the term shows up in a document. */
  example: string;
  /** How to read that example, in plain English. */
  reading: string;
  /** The mistake buyers and owners most often make with this term. */
  pitfall: string;
  /** Concrete actions, in order. */
  nextSteps: string[];
};

export const GLOSSARY_DEPTH: Record<string, GlossaryDepth> = {
  "blanket-easement": {
    example:
      "\u201cEasement granted to Rural Electric Cooperative over, under and across the above described premises, recorded in Book 214, Page 77, no specific location described.\u201d",
    reading:
      "The phrase that matters is \u201cno specific location described.\u201d The utility holds rights across the whole parcel, not a measured strip, so no square foot of the lot is provably outside the easement until the holder agrees in writing to a defined corridor.",
    pitfall:
      "Assuming the poles and lines you can see mark the limits of the easement. The visible equipment shows where the utility went; the recorded document lets it go somewhere else on the same parcel later.",
    nextSteps: [
      "Pull the recorded instrument by book and page from the county recorder and read what uses it allows.",
      "Ask the utility for a partial release or a consent letter fixing the easement to the strip it currently occupies.",
      "Do not site a pool, shop, or addition anywhere on the parcel until that release is recorded.",
    ],
  },
  "conservation-easement": {
    example:
      "\u201cDeed of Conservation Easement in favor of the County Land Trust, recorded in Book 1188, Page 402, restricting subdivision and limiting improvements to the designated building envelope.\u201d",
    reading:
      "This is a restriction on you, not a right of passage for someone else. The building envelope named in the deed \u2014 not the zoning code \u2014 controls where and how much you may build, permanently.",
    pitfall:
      "Reading the acreage on the listing and assuming it is developable. A prior owner may have taken a tax deduction in exchange for giving up the subdivision rights you were counting on.",
    nextSteps: [
      "Get the full easement deed and its exhibit map, which show the permitted building envelope.",
      "Contact the holding land trust or agency to confirm current interpretation and any approved amendments.",
      "Confirm any planned structure fits the envelope before you write an offer, not after inspection.",
    ],
  },
  survey: {
    example:
      "\u201cALTA/NSPS Land Title Survey prepared by a licensed surveyor, showing all easements of record per Schedule B-II, encroachments, and improvements.\u201d",
    reading:
      "A survey is the only document that puts recorded easements in their real position on the ground. Everything else \u2014 the commitment, the plat, an online parcel viewer \u2014 tells you an easement exists without telling you where.",
    pitfall:
      "Treating a county parcel viewer as a survey. Those boundaries are digitized from tax maps and can be off by many feet, which is more than enough to put a planned fence inside a corridor.",
    nextSteps: [
      "Order the survey during your inspection period so the results can still affect the deal.",
      "Give the surveyor the title commitment so every Schedule B-II exception gets plotted.",
      "Compare the plotted easements against your build plan before you request permits.",
    ],
  },
  easement: {
    example:
      "\u201cSubject to a 10 foot easement for ingress, egress and utilities along the southerly line of Lot 12, as shown on the recorded plat.\u201d",
    reading:
      "Three facts define any easement: who holds it, what they may do, and where. Here it is a 10-foot strip along one boundary usable for both travel and utilities \u2014 which means the strip must stay clear and reachable.",
    pitfall:
      "Believing an easement can be removed by buying the property or fencing the strip. Recorded easements run with the land and survive every sale.",
    nextSteps: [
      "Identify the holder and the recorded document number for each easement on Schedule B-II.",
      "Map the strips against your lot to find what is left over \u2014 the buildable envelope.",
      "Where an easement looks abandoned, ask the holder for a written release; do not assume disuse ends it.",
    ],
  },
  "appurtenant-easement": {
    example:
      "\u201cTogether with a perpetual non-exclusive easement for ingress and egress over the westerly 20 feet of Lot 4 for the benefit of Lot 5.\u201d",
    reading:
      "\u201cFor the benefit of Lot 5\u201d makes this appurtenant: the right belongs to a parcel, not a person. Lot 4 is burdened forever, Lot 5 is benefited forever, and both pass automatically at every future sale.",
    pitfall:
      "Buying the burdened lot and expecting to renegotiate the driveway with the new neighbor. There is nothing to renegotiate \u2014 the right is already theirs.",
    nextSteps: [
      "Determine whether the parcel you are buying is the benefited or the burdened one.",
      "Check whether the recorded route matches the drive actually in use; a mismatch is a future dispute.",
      "Read the document for maintenance and cost-sharing terms, which are commonly silent.",
    ],
  },
  "easement-in-gross": {
    example:
      "\u201cEasement to Metro Gas Company, its successors and assigns, recorded in Book 902, Page 15.\u201d",
    reading:
      "\u201cSuccessors and assigns\u201d with no benefited parcel named marks this as an easement in gross. The right belongs to the company and travels with the company, so a merger or sale of the network carries it forward.",
    pitfall:
      "Assuming a defunct or renamed utility ends the easement. The right transfers with the corporate assets.",
    nextSteps: [
      "Trace who currently holds the easement \u2014 the named company may no longer exist under that name.",
      "Request the holder's construction standards, which state what may be placed inside the strip.",
      "Route fencing and landscaping around the corridor rather than inside it.",
    ],
  },
  "utility-easement": {
    example:
      "\u201cA 15 foot utility easement along the rear lot line as shown on the plat of Cedar Ridge, Section 2, for installation and maintenance of electric, gas, water, sewer and communication facilities.\u201d",
    reading:
      "Fifteen feet along the rear line means the back of the lot is working ground for the utility. Maintenance rights include digging, trimming, and bringing equipment through, so anything permanent inside the strip is at risk.",
    pitfall:
      "Building a shed, pool deck, or retaining wall inside the strip because nothing is visible above ground. Buried lines are the most common reason a homeowner is billed for removing their own improvement.",
    nextSteps: [
      "Call 811 for a free utility locate before any digging, regardless of what the plat shows.",
      "Measure the easement width from the correct line \u2014 usually the rear or side lot line, not the fence.",
      "Move planned structures outside the strip, or ask the utility whether a removable improvement is acceptable.",
    ],
  },
  "right-of-way": {
    example:
      "\u201cSubject to the right-of-way of County Road 18, being 60 feet in width, 30 feet on each side of the centerline.\u201d",
    reading:
      "A 60-foot right-of-way on a road with 22 feet of pavement means roughly 19 feet of what looks like your front yard on each side is inside public right-of-way. Setbacks get measured from that line, not from the asphalt.",
    pitfall:
      "Measuring a front setback from the curb or the edge of pavement. That single error is enough to have a permit denied or a finished addition flagged.",
    nextSteps: [
      "Find the right-of-way width in the plat or road dedication and locate its edge on your lot.",
      "Measure your front setback from the right-of-way line, then add it to the right-of-way depth.",
      "Check with the road authority before placing mailboxes, fences, or drainage inside the strip.",
    ],
  },
  "prescriptive-easement": {
    example:
      "\u201cRights of parties in possession and matters which an accurate survey and inspection of the premises would disclose.\u201d",
    reading:
      "That standard exception is where prescriptive claims hide. Nothing names the neighbor's two-track across the back forty, but the insurer has excluded exactly that kind of unrecorded, use-based right from coverage.",
    pitfall:
      "Relying on a clean title search. Prescriptive rights are created by conduct on the ground and never appear in the records until someone sues.",
    nextSteps: [
      "Walk the property looking for worn paths, utility runs, or drives that serve someone else.",
      "Ask the seller in writing whether anyone else uses any part of the land, and for how long.",
      "If a use exists, get a written, signed permission or license \u2014 permission defeats a prescriptive claim.",
    ],
  },
  "easement-by-necessity": {
    example:
      "\u201cThe subject parcel was severed from the parent tract by deed recorded in Book 331, Page 20, and has no frontage on a dedicated public road.\u201d",
    reading:
      "Severance plus no road frontage is the fact pattern that creates access by necessity. A court can imply a route across the retained land even though no easement was ever written.",
    pitfall:
      "Buying the retained parcel and assuming the landlocked neighbor has no rights because nothing is recorded. Necessity does not require a document.",
    nextSteps: [
      "Trace the chain of title to find when and how the parcels were split.",
      "Confirm every neighboring parcel has legal \u2014 not just practical \u2014 access to a public road.",
      "Where necessity is likely, negotiate and record a defined route now rather than litigate it later.",
    ],
  },
  "schedule-b": {
    example:
      "\u201cSchedule B \u2014 Section II Exceptions: 7. Easement granted to City of Springfield recorded 06/14/1978 in Book 512, Page 331. 8. Restrictions recorded in Book 498, Page 12.\u201d",
    reading:
      "Each numbered line is a limitation the insurer will not cover. A bare book-and-page citation is not a description \u2014 you have to pull the referenced instrument to learn the width, the location, and what it prohibits.",
    pitfall:
      "Skimming Schedule B because it is short. Two lines of citation can conceal a transmission corridor across the middle of the lot.",
    nextSteps: [
      "Number every exception and request the underlying recorded document for each one.",
      "Sort them into use restrictions, monetary liens, and standard boilerplate.",
      "Raise anything that touches your build plan before your objection deadline expires.",
    ],
  },
  "title-commitment": {
    example:
      "\u201cCommitment for Title Insurance: Schedule A \u2014 proposed insured, policy amount, legal description; Schedule B-I \u2014 requirements; Schedule B-II \u2014 exceptions.\u201d",
    reading:
      "Schedule A tells you what is being insured and for how much. B-I is the to-do list before closing. B-II is the list of what the policy will never cover \u2014 which is where the easements live.",
    pitfall:
      "Waiting until the closing table. The commitment is issued precisely so problems can be raised while you still have the leverage to walk or renegotiate.",
    nextSteps: [
      "Read the legal description against the address and the plat to confirm it is the right parcel.",
      "Satisfy or waive each B-I requirement knowingly, not by default.",
      "Compare B-II against your intended use and object in writing before the deadline.",
    ],
  },
  "title-policy-exception": {
    example:
      "\u201cException 5: Any encroachment, encumbrance, violation, variation, or adverse circumstance affecting the title that would be disclosed by an accurate and complete land survey.\u201d",
    reading:
      "This is a standard exception. Everything a survey would have revealed is carved out of coverage \u2014 which is the insurer telling you, in advance, to get a survey.",
    pitfall:
      "Declining the survey to save money, then discovering the fence is over the line with no coverage to fall back on.",
    nextSteps: [
      "Ask which standard exceptions can be deleted by delivering a current survey or an owner's affidavit.",
      "Price extended coverage against the cost of the survey \u2014 often they are close.",
      "Keep the final policy with the survey; a future buyer will ask for both.",
    ],
  },
  encumbrance: {
    example:
      "\u201cThe premises are conveyed subject to all easements, restrictions, covenants, liens, and encumbrances of record.\u201d",
    reading:
      "A catch-all clause like this transfers every recorded burden to you without listing any of them. The full inventory is in Schedule B-II, not in the deed.",
    pitfall:
      "Treating \u201csubject to encumbrances of record\u201d as boilerplate. It is the sentence that makes someone else's rights your problem.",
    nextSteps: [
      "Build one list of every encumbrance: easements, liens, mortgages, leases, covenants, judgments.",
      "Separate what must be paid off at closing from what will survive it.",
      "Confirm any released item actually has a recorded release, not just a promise.",
    ],
  },
  encroachment: {
    example:
      "\u201cSurvey discloses a wood fence located 2.6 feet east of the record boundary line, and a concrete drive extending 1.4 feet into the adjoining parcel.\u201d",
    reading:
      "Nothing here is in the title records \u2014 it took a survey to find. The measurement matters: an encroachment left in place long enough can ripen into a prescriptive right or an ownership claim.",
    pitfall:
      "Letting a small encroachment ride because the neighbor is friendly. Friendliness is not recorded; the fence line is what a future court and a future buyer will see.",
    nextSteps: [
      "Get a boundary survey that stakes the corners, not just a sketch.",
      "Resolve it in writing: a boundary line agreement, a recorded license, or removal before closing.",
      "Confirm the title company will insure over anything you agree to leave in place.",
    ],
  },
  setback: {
    example:
      "\u201cMinimum setbacks: front 25 feet from the right-of-way line, side 8 feet, rear 20 feet, corner side 15 feet.\u201d",
    reading:
      "These are the zoning minimums, and they are measured from lot lines and right-of-way lines. Easements are separate and stack on top: a corridor sitting inside the setback does not shrink the setback, but a wider one pushes the buildable line further in.",
    pitfall:
      "Measuring from the fence, the curb, or the neighbor's structure instead of the platted line.",
    nextSteps: [
      "Get the current setbacks from the zoning ordinance for your specific district, not a general summary.",
      "Check subdivision covenants, which frequently impose stricter setbacks than zoning does.",
      "Draw setbacks and easements together before designing anything.",
    ],
  },
  "buildable-envelope": {
    example:
      "\u201cLot 7: 0.34 acres. Front setback 25 ft, side 8 ft, rear 20 ft. Rear 15 ft encumbered by utility easement; 20 ft drainage easement across the northeast corner.\u201d",
    reading:
      "Subtract the setbacks, then subtract the easements that prohibit structures. On a lot this size that arithmetic can leave a footprint far smaller than the acreage suggests \u2014 and the leftover shape, not just its area, decides what fits.",
    pitfall:
      "Comparing lots by acreage. Two identical-size lots can have completely different envelopes once corridors are removed.",
    nextSteps: [
      "Draw the parcel to scale, then shade every setback and easement.",
      "Test your actual footprint \u2014 house, driveway, pool deck \u2014 inside what remains.",
      "Have a surveyor confirm the envelope before you commit to a plan or an offer.",
    ],
  },
  "dominant-estate": {
    example:
      "\u201cFor the benefit of Lot 5 and its successors in title, a perpetual easement for ingress and egress over the driveway shown on the plat.\u201d",
    reading:
      "Lot 5 is the dominant estate: it holds the right. Buying Lot 5 buys guaranteed access, provided the recorded route is the one that physically exists.",
    pitfall:
      "Assuming the driveway you drove in on is the recorded route. When the paved drive drifted off the easement years ago, the recorded right may cross ground you cannot actually use.",
    nextSteps: [
      "Overlay the recorded easement description on a survey of the drive in use.",
      "Confirm the easement covers the uses you need \u2014 utilities as well as travel, if applicable.",
      "Clarify who maintains and repairs the surface, and record it if the document is silent.",
    ],
  },
  "servient-estate": {
    example:
      "\u201cSubject to an easement over the westerly 20 feet for the benefit of the adjoining parcel; the servient owner shall not obstruct the easement area.\u201d",
    reading:
      "You own the 20 feet and pay taxes on it, but \u201cshall not obstruct\u201d controls what you can put there. Gates, plantings, parked vehicles, and grade changes can all count as obstruction.",
    pitfall:
      "Fencing the strip for privacy. Blocking the easement is the fastest route to an injunction and the neighbor's legal fees.",
    nextSteps: [
      "Read the exact scope: what uses are allowed, and at what hours or intensity.",
      "Ask whether a gate or removable barrier is permitted, and get any agreement in writing.",
      "Confirm who pays for maintenance and for damage caused by the holder's use.",
    ],
  },
  "restrictive-covenant": {
    example:
      "\u201cNo detached outbuilding shall exceed 120 square feet, and no structure shall be erected without prior written approval of the Architectural Review Committee.\u201d",
    reading:
      "This is private contract law, enforceable by neighbors or an HOA, and it is stricter than the zoning code. Approval is a separate step from your building permit \u2014 you can hold one and be blocked by the other.",
    pitfall:
      "Getting a permit from the county and starting work. The permit says government rules allow it; the covenant is what your neighbors can sue on.",
    nextSteps: [
      "Get the full covenant document plus every recorded amendment, not the HOA's summary.",
      "Check for architectural review requirements and how long approval takes.",
      "Verify whether the covenants have an expiration or renewal term.",
    ],
  },
  "adverse-possession": {
    example:
      "\u201cPossession of the disputed strip has been actual, open, notorious, exclusive, continuous, and hostile since 1998, and taxes thereon have been paid by the claimant.\u201d",
    reading:
      "Every one of those adjectives is a required element, and the last clause matters in states that also require tax payment or color of title. Satisfying all of them moves ownership, not merely a right to use.",
    pitfall:
      "Confusing this with a prescriptive easement. A prescriptive easement gives someone the right to cross; adverse possession takes the land.",
    nextSteps: [
      "Survey the boundary and compare it to fences, hedges, and mowing lines.",
      "Interrupt a running claim promptly \u2014 a written permission or a timely objection resets the analysis.",
      "Talk to a real estate attorney in your state before a long-standing occupation reaches the statutory term.",
    ],
  },
};

export function glossaryDepthFor(slug: string): GlossaryDepth | undefined {
  return GLOSSARY_DEPTH[slug];
}
